// WhatsApp Service for Frontend
export interface WhatsAppMessage {
  id: string;
  to: string;
  from: string;
  message: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  error?: string;
  message_id?: string;
}

export interface SendWhatsAppRequest {
  to: string;
  message?: string;
  template_id?: string;
  variables?: Record<string, string>;
  is_template?: boolean;
}

export interface WhatsAppResponse {
  success: boolean;
  message_id?: string;
  message?: string;
  error?: string;
}

class WhatsAppService {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/whatsapp') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return localStorage.getItem('token') || '';
  }

  /**
   * Send a single WhatsApp message
   */
  async sendMessage(request: SendWhatsAppRequest): Promise<WhatsAppResponse> {
    const response = await fetch(`${this.baseUrl}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send WhatsApp message: ${error}`);
    }

    const data: WhatsAppResponse = await response.json();
    return data;
  }

  /**
   * Send a WhatsApp template message
   */
  async sendTemplate(request: SendWhatsAppRequest): Promise<WhatsAppResponse> {
    const response = await fetch(`${this.baseUrl}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify({
        ...request,
        is_template: true,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send WhatsApp template: ${error}`);
    }

    const data: WhatsAppResponse = await response.json();
    return data;
  }

  /**
   * Validate phone number format (E.164)
   */
  isValidPhoneNumber(phone: string): boolean {
    // E.164 format: +[country code][number]
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  }

  /**
   * Format phone number to E.164 format
   */
  formatPhoneNumber(phone: string): string {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If doesn't start with country code, assume default (you might want to make this configurable)
    if (!cleaned.startsWith('1') && !cleaned.startsWith('234')) {
      // Default to Nigeria (+234) if no country code
      cleaned = '234' + cleaned;
    }
    
    return '+' + cleaned;
  }
}

// Export singleton instance
export const whatsappService = new WhatsAppService();

